import React from 'react';
import { Image } from 'antd';
import { MessageQuestionIcon } from 'icons';
import { useTranslations } from 'next-intl';

import { useAppDispatch } from 'store';

import { setQuestionId, setTestFeedbackModal } from 'modules/global';
import { IOption, ISubQuestion } from 'modules/national-certificate';
import { useSelectedOption } from 'modules/national-certificate-test';

interface IProps {
  question?: ISubQuestion;
  index: number;
  length?: number;
  selectedOptionId?: number | null;
  allOptions: IOption[];
}

const Questions: React.FC<IProps> = ({ question, index, length, selectedOptionId, allOptions }) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();

  const { optionId: selectedId, letter: selectedLetter } = useSelectedOption({
    selectedOptionId,
    checked: question?.checked,
    allOptions
  });

  const correctOption = question?.options?.find(opt => opt.is_correct);
  const correctIndex = question?.options?.indexOf(correctOption!);
  const correctLetter = correctOption ? String.fromCharCode(65 + correctIndex!) : null;

  const onOpenFeedbackModal = () => {
    dispatch(setQuestionId(question?.question_id as number));
    dispatch(setTestFeedbackModal(true));
  };

  const renderLetters = () => {
    if (!selectedId) {
      return (
        <div className="flex items-center gap-2 rounded-sm bg-white px-2.5 py-1 text-sm font-semibold text-green-500">
          {correctLetter}
        </div>
      );
    }

    if (selectedId === correctOption?.id) {
      return (
        <div className="flex items-center gap-2 rounded-sm bg-white px-2.5 py-1 text-sm font-semibold text-green-500">
          {selectedLetter}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 rounded-sm bg-white px-2.5 py-1 text-sm font-semibold text-red-500">
          {selectedLetter}
        </div>
        <div className="flex items-center gap-1 rounded-sm bg-white px-2.5 py-1 text-sm font-semibold text-green-500">
          {correctLetter}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl bg-gray-50 p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400">
          {t('savol')}: {index + 1}/{length}
        </div>
        <div onClick={onOpenFeedbackModal} className="flex cursor-pointer items-center gap-2 text-orange-400">
          <MessageQuestionIcon />
        </div>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <div className="whitespace-pre-line text-sm">{question?.question_name}</div>
        {question?.image && (
          <div className="mt-2 flex justify-center">
            <Image src={question?.image} alt={question?.name || 'Test option'} loading="lazy" title="Test image" />
          </div>
        )}
        {renderLetters()}
      </div>
    </div>
  );
};

export default Questions;
